import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo } from "react";
import { Handle, Position, useNodeId } from "@xyflow/react";
import { useTranslation } from "react-i18next";
import { OVERLAY_COLOR_CLASSES, getAssigneeName } from "../utils";
import { NodeOverlayLabel } from "./NodeOverlayLabel";
import { useIsContextMenuActiveNode, useIsDropTarget } from "../../../flow-kit";
import { useNodeVisualState } from "../hooks/useNodeVisualState";
const nodeWidth = 180;
const nodeHeight = 90;
function ProcessFlowActivityNode({ data, dragging }) {
    const { t } = useTranslation();
    const nodeId = useNodeId();
    const isDropTarget = useIsDropTarget(nodeId);
    const isContextMenuActive = useIsContextMenuActiveNode(nodeId);
    const isEdit = data?.mode === "edit";
    const { state, tokens, isDashed, dimClass, handleMouseEnter, handleMouseLeave } = useNodeVisualState({
        isSelected: data?.isSelected,
        isContextMenuActive,
        isCurrentStep: data?.isCurrentStep,
        isOnPath: data?.isOnPath,
        isDimmed: data?.isDimmed,
        isEdit,
        hasPublishError: data?.hasPublishError,
        isOrphaned: data?.isOrphaned,
        isSkeleton: data?.isSkeleton
    });
    if (!data) return null;
    const { stepRecording } = data;
    const displayName = stepRecording.generatedName || stepRecording.task?.taskDescription || "";
    const inputs = stepRecording.inputs || [];
    const sourceHandleDistance = nodeHeight / (inputs.length + 1);
    // Overlay background only wins when no state token has claimed the card.
    const bgClass = state === "default" && data.overlayColor ? OVERLAY_COLOR_CLASSES[data.overlayColor] : tokens.bgClass;
    const borderStyleClass = isDashed ? "border-dashed" : "border-solid";
    const cursorClass = dragging ? "cursor-grabbing" : isEdit ? "cursor-grab" : "cursor-pointer";
    return /*#__PURE__*/ _jsxs("div", {
        className: `relative transition-opacity duration-200 ${dimClass}`,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        children: [
            inputs.length === 0 ? /*#__PURE__*/ _jsx(Handle, {
                type: "target",
                position: Position.Left,
                isConnectable: isEdit
            }) : inputs.map((_, index)=>/*#__PURE__*/ _jsx(Handle, {
                    type: "target",
                    position: Position.Left,
                    id: `handle-${index}`,
                    style: {
                        top: sourceHandleDistance * (index + 1)
                    },
                    isConnectable: isEdit
                }, index)),
            isEdit && /*#__PURE__*/ _jsx(Handle, {
                type: "target",
                position: Position.Left,
                id: "editor-target",
                style: {
                    top: nodeHeight / 2,
                    opacity: 0
                }
            }),
            /*#__PURE__*/ _jsx("div", {
                className: `border-2 ${borderStyleClass} ${tokens.borderClass} ${bgClass} ${tokens.textClass} rounded-md ${cursorClass} ${isDropTarget ? "ring-2 ring-info-solid ring-offset-1" : tokens.ringClass}`,
                style: {
                    width: nodeWidth,
                    height: nodeHeight,
                    textAlign: "center",
                    fontSize: 12,
                    display: "flex",
                    alignItems: "center",
                    padding: 6,
                    wordBreak: "break-word"
                },
                children: displayName ? `${displayName.slice(0, 70)}${displayName.length > 70 ? "..." : ""}` : isEdit ? /*#__PURE__*/ _jsxs("span", {
                    className: "text-fg-subtle italic",
                    children: [
                        stepRecording.actionType === "decision" ? t("processFlowEditor.newDecision") : t("processFlowEditor.newTask"),
                        (()=>{
                            const name = getAssigneeName(stepRecording);
                            return name ? ` — ${name}` : "";
                        })()
                    ]
                }) : ""
            }),
            stepRecording.stepNumber != null && /*#__PURE__*/ _jsx("div", {
                className: "absolute -top-2 -left-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-fg-secondary text-white text-3xs font-semibold leading-none ring-2 ring-white pointer-events-none",
                "aria-hidden": true,
                children: stepRecording.stepNumber
            }),
            /*#__PURE__*/ _jsx(NodeOverlayLabel, {
                color: data.overlayColor,
                label: data.overlayLabel,
                badges: data.overlayBadges,
                anchor: {
                    top: -8,
                    right: -8
                }
            }),
            /*#__PURE__*/ _jsx(Handle, {
                type: "source",
                position: Position.Right,
                isConnectable: isEdit
            })
        ]
    });
}
export default /*#__PURE__*/ memo(ProcessFlowActivityNode);

//# sourceMappingURL=ActivityNode.js.map